<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import CampaignAnalyticsLayout from 'dashboard/components-next/Campaigns/CampaignAnalyticsLayout.vue';
import WhatsappCampaignsAPI from 'custom/api/whatsappCampaigns';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const campaigns = useMapGetter('campaigns/getAllCampaigns');

const ROW_STATUSES = [
  'all',
  'pending',
  'processing',
  'sent',
  'failed',
  'unknown',
];
const rowStatusLabels = {
  pending: t('CAMPAIGN.WHATSAPP.MASS.STATUS.ROWS.PENDING'),
  processing: t('CAMPAIGN.WHATSAPP.MASS.STATUS.ROWS.PROCESSING'),
  sent: t('CAMPAIGN.WHATSAPP.MASS.STATUS.ROWS.SENT'),
  failed: t('CAMPAIGN.WHATSAPP.MASS.STATUS.ROWS.FAILED'),
  unknown: t('CAMPAIGN.WHATSAPP.MASS.STATUS.ROWS.UNKNOWN'),
};
const batchStatusLabels = {
  staging: t('CAMPAIGN.WHATSAPP.MASS.STATUS.BATCH.STAGING'),
  validated: t('CAMPAIGN.WHATSAPP.MASS.STATUS.BATCH.VALIDATED'),
  confirmed: t('CAMPAIGN.WHATSAPP.MASS.STATUS.BATCH.CONFIRMED'),
  processing: t('CAMPAIGN.WHATSAPP.MASS.STATUS.BATCH.PROCESSING'),
  paused: t('CAMPAIGN.WHATSAPP.MASS.STATUS.BATCH.PAUSED'),
  completed: t('CAMPAIGN.WHATSAPP.MASS.STATUS.BATCH.COMPLETED'),
  failed: t('CAMPAIGN.WHATSAPP.MASS.STATUS.BATCH.FAILED'),
};
const state = ref({ import: null, rows: [], meta: {} });
const status = ref('all');
const page = ref(1);
const loading = ref(true);
const error = ref('');
let pollTimer;

const campaignId = computed(() => Number(route.params.campaignId));
const campaign = computed(() =>
  campaigns.value.find(record => Number(record.id) === campaignId.value)
);
const pendingCount = computed(() => {
  const current = state.value.import;
  if (!current) return 0;
  return Math.max(
    Number(current.valid_rows || 0) -
      Number(current.sent_rows || 0) -
      Number(current.failed_rows || 0) -
      Number(current.unknown_rows || 0),
    0
  );
});
const canResume = computed(
  () => state.value.import?.status === 'paused' && pendingCount.value > 0
);
const totalRows = computed(() => Number(state.value.meta.total_count || 0));
const statusOptions = computed(() =>
  ROW_STATUSES.map(value => ({
    value,
    label:
      value === 'all'
        ? t('CAMPAIGN.WHATSAPP.MASS.STATUS.ALL')
        : rowStatusLabels[value],
  }))
);
const breadcrumbItems = computed(() => [
  { label: t('CAMPAIGN.WHATSAPP.ANALYTICS.BREADCRUMB.CAMPAIGNS') },
  { label: t('CAMPAIGN.WHATSAPP.ANALYTICS.BREADCRUMB.WHATSAPP') },
  { label: campaign.value?.title || `#${campaignId.value}` },
]);

const statusLabel = value => rowStatusLabels[value] || value;

const rowError = row =>
  row.errors
    ?.map(item => item.message)
    .filter(Boolean)
    .join('; ') || '-';

const fetchStatus = async () => {
  loading.value = true;
  error.value = '';
  try {
    const response = await WhatsappCampaignsAPI.status(campaignId.value, {
      page: page.value,
      status: status.value === 'all' ? undefined : status.value,
    });
    state.value = response.data;
  } catch (requestError) {
    error.value =
      requestError?.response?.data?.error ||
      t('CAMPAIGN.WHATSAPP.MASS.STATUS.API.ERROR_MESSAGE');
  } finally {
    loading.value = false;
  }
};

const stopPolling = () => {
  window.clearTimeout(pollTimer);
  pollTimer = undefined;
};

const schedulePolling = () => {
  stopPolling();
  if (!['confirmed', 'processing'].includes(state.value.import?.status)) return;
  pollTimer = window.setTimeout(async () => {
    await fetchStatus();
    schedulePolling();
  }, 5000);
};

const handleFilterChange = event => {
  status.value = event.target.value;
  page.value = 1;
};

const resume = async () => {
  if (!canResume.value) return;
  try {
    const response = await WhatsappCampaignsAPI.resume(campaignId.value);
    state.value = response.data;
    useAlert(t('CAMPAIGN.WHATSAPP.MASS.STATUS.API.RESUME_SUCCESS'));
    schedulePolling();
  } catch (requestError) {
    useAlert(
      requestError?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.MASS.STATUS.API.ERROR_MESSAGE')
    );
  }
};

const goToCampaigns = () => router.push({ name: 'campaigns_whatsapp_index' });

watch(
  () => [campaignId.value, status.value, page.value],
  () => {
    fetchStatus();
  },
  { immediate: true }
);

watch(() => state.value.import?.status, schedulePolling);

onBeforeUnmount(stopPolling);
</script>

<template>
  <CampaignAnalyticsLayout
    :breadcrumb-items="breadcrumbItems"
    @breadcrumb-click="goToCampaigns"
  >
    <div class="flex flex-col gap-6 pb-8">
      <div class="flex items-center justify-between gap-3">
        <div class="flex flex-col gap-1">
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.MASS.STATUS.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-11">
            {{ campaign?.title || `#${campaignId}` }}
          </p>
        </div>
        <Button
          v-if="canResume"
          :label="t('CAMPAIGN.WHATSAPP.MASS.STATUS.RESUME')"
          @click="resume"
        />
      </div>

      <div
        v-if="error"
        class="p-4 text-sm rounded-lg bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ error }}
      </div>

      <div
        v-if="loading && !state.import"
        class="flex items-center justify-center min-h-48 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else-if="state.import">
        <div
          v-if="state.import.status === 'paused'"
          class="flex items-center gap-3 p-4 text-sm rounded-lg bg-n-amber-3 text-n-amber-12"
        >
          <Icon icon="i-lucide-pause-circle" class="size-4 shrink-0" />
          <span>{{
            state.import.last_error || t('CAMPAIGN.WHATSAPP.MASS.STATUS.PAUSED')
          }}</span>
        </div>
        <div
          v-else-if="state.import.status === 'failed'"
          class="p-4 text-sm rounded-lg bg-n-ruby-9/20 text-n-ruby-9"
        >
          {{
            state.import.last_error || t('CAMPAIGN.WHATSAPP.MASS.STATUS.FAILED')
          }}
        </div>

        <div
          class="grid grid-cols-2 gap-px overflow-hidden border rounded-xl sm:grid-cols-3 lg:grid-cols-6 bg-n-weak border-n-weak"
        >
          <div class="flex flex-col gap-2 p-4 bg-n-solid-1">
            <span class="text-xs text-n-slate-11">{{
              t('CAMPAIGN.WHATSAPP.MASS.STATUS.TOTAL')
            }}</span>
            <span class="text-2xl font-semibold tabular-nums text-n-slate-12">{{
              state.import.total_rows
            }}</span>
          </div>
          <div class="flex flex-col gap-2 p-4 bg-n-solid-1">
            <span class="text-xs text-n-slate-11">{{
              t('CAMPAIGN.WHATSAPP.MASS.STATUS.PENDING')
            }}</span>
            <span class="text-2xl font-semibold tabular-nums text-n-slate-12">{{
              pendingCount
            }}</span>
          </div>
          <div class="flex flex-col gap-2 p-4 bg-n-solid-1">
            <span class="text-xs text-n-slate-11">{{
              t('CAMPAIGN.WHATSAPP.MASS.STATUS.SENT')
            }}</span>
            <span class="text-2xl font-semibold tabular-nums text-n-slate-12">{{
              state.import.sent_rows
            }}</span>
          </div>
          <div class="flex flex-col gap-2 p-4 bg-n-solid-1">
            <span class="text-xs text-n-slate-11">{{
              t('CAMPAIGN.WHATSAPP.MASS.STATUS.FAILED_COUNT')
            }}</span>
            <span class="text-2xl font-semibold tabular-nums text-n-slate-12">{{
              state.import.failed_rows
            }}</span>
          </div>
          <div class="flex flex-col gap-2 p-4 bg-n-solid-1">
            <span class="text-xs text-n-slate-11">{{
              t('CAMPAIGN.WHATSAPP.MASS.STATUS.UNKNOWN')
            }}</span>
            <span class="text-2xl font-semibold tabular-nums text-n-slate-12">{{
              state.import.unknown_rows
            }}</span>
          </div>
          <div class="flex flex-col gap-2 p-4 bg-n-solid-1">
            <span class="text-xs text-n-slate-11">{{
              t('CAMPAIGN.WHATSAPP.MASS.STATUS.STATE')
            }}</span>
            <span class="text-sm font-medium text-n-slate-12">{{
              batchStatusLabels[state.import.status] || state.import.status
            }}</span>
          </div>
        </div>

        <div
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <span class="text-sm font-medium text-n-slate-12">{{
              t('CAMPAIGN.WHATSAPP.MASS.STATUS.ROWS_TITLE')
            }}</span>
            <select
              :value="status"
              class="px-3 py-2 text-sm border rounded-md outline-none bg-n-alpha-2 border-n-weak text-n-slate-12"
              @change="handleFilterChange"
            >
              <option
                v-for="option in statusOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <div
            v-if="loading"
            class="flex items-center justify-center py-12 text-n-slate-11"
          >
            <Spinner />
          </div>
          <div v-else class="overflow-x-auto border rounded-md border-n-weak">
            <table class="w-full text-sm text-start">
              <thead class="text-xs text-n-slate-11 bg-n-alpha-2">
                <tr>
                  <th class="px-3 py-2 font-medium">
                    {{ t('CAMPAIGN.WHATSAPP.MASS.TABLE.ROW') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('CAMPAIGN.WHATSAPP.MASS.TABLE.PHONE') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('CAMPAIGN.WHATSAPP.MASS.TABLE.CONTACT') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('CAMPAIGN.WHATSAPP.MASS.TABLE.MESSAGE') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('CAMPAIGN.WHATSAPP.MASS.TABLE.STATUS') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('CAMPAIGN.WHATSAPP.MASS.TABLE.ERROR') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in state.rows"
                  :key="row.id"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2 tabular-nums text-n-slate-11">
                    {{ row.row_number }}
                  </td>
                  <td class="px-3 py-2 tabular-nums text-n-slate-11">
                    {{ row.phone_number || '-' }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-12">
                    {{
                      row.contact?.name ||
                      t('CAMPAIGN.WHATSAPP.MASS.TABLE.NEW_RECIPIENT')
                    }}
                  </td>
                  <td
                    class="max-w-64 px-3 py-2 whitespace-pre-wrap text-n-slate-12"
                  >
                    {{ row.message || '-' }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{ statusLabel(row.status) }}
                  </td>
                  <td class="px-3 py-2 text-n-ruby-9">{{ rowError(row) }}</td>
                </tr>
                <tr v-if="!state.rows.length">
                  <td colspan="6" class="px-3 py-8 text-center text-n-slate-11">
                    {{ t('CAMPAIGN.WHATSAPP.MASS.STATUS.EMPTY') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <PaginationFooter
            v-if="totalRows > 100"
            :current-page="page"
            :total-items="totalRows"
            :items-per-page="100"
            current-page-info="CAMPAIGN.WHATSAPP.MASS.STATUS.PAGE_INFO"
            class="!bg-transparent !px-0 rounded-b-xl before:hidden"
            @update:current-page="page = $event"
          />
        </div>
      </template>
    </div>
  </CampaignAnalyticsLayout>
</template>
